import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  Anchor, LayoutDashboard, Ship, Calendar, CalendarCheck, MessageSquare, 
  FileText, LogOut, Settings, Menu, X, PenSquare, DollarSign, FileSignature, Tag, Building2, ScrollText, Users, CreditCard, Sparkles
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = ({ children, title }) => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Ship, label: 'Croisières', path: '/admin/cruises' },
    { icon: Calendar, label: 'Planning', path: '/admin/planning' },
    { icon: DollarSign, label: 'Financier', path: '/admin/financier' },
    { icon: CalendarCheck, label: 'Réservations', path: '/admin/bookings' },
    { icon: FileText, label: 'Demandes devis', path: '/admin/quotes' },
    { icon: FileSignature, label: 'Devis', path: '/admin/devis' },
    { icon: CreditCard, label: 'Commandes', path: '/admin/commandes' },
    { icon: Tag, label: 'Offres', path: '/admin/offres' },
    { icon: Sparkles, label: 'Dernière minute', path: '/admin/last-minute' },
    { icon: Building2, label: 'Agences', path: '/admin/agences' },
    { icon: Users, label: 'Clients', path: '/admin/clients' },
    { icon: ScrollText, label: 'Contrats Proprios', path: '/admin/owner-contracts' },
    { icon: MessageSquare, label: 'Messages', path: '/admin/messages' },
    { icon: PenSquare, label: 'Contenu (CMS)', path: '/admin/pages' },
  ];

  const isActive = (path) => location.pathname === path;

  if (loading || !isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#0A2540] z-50 flex items-center justify-between px-4">
        <Link to="/admin/dashboard" className="flex items-center gap-2">
          <Anchor className="h-6 w-6 text-[#D4AF37]" />
          <span className="text-white font-display font-bold">Admin</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-2"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 bottom-0 w-64 bg-[#0A2540] z-40 transform transition-transform lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6 flex-shrink-0">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
              <Anchor className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-white font-display font-bold">SOGNUDIMARE</span>
              <span className="block text-xs text-white/50">Administration</span>
            </div>
          </Link>
        </div>

        <nav className="px-4 space-y-1 flex-1 overflow-y-auto pb-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-[#D4AF37] text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-2 text-white/70 text-sm">
            <div className="w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center text-white font-bold">
              {user?.name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-white/50 text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 mt-2"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
          <Link 
            to="/" 
            className="block mt-2 px-4 py-2 text-sm text-white/50 hover:text-white transition-colors"
          >
            ← Retour au site
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4">
          <h1 className="font-display text-xl sm:text-2xl font-bold text-[#0A2540]">{title}</h1>
        </header>

        {/* Content */}
        <div className="p-3 sm:p-6">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
